import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { businessPhoneSchema } from "./settings.js";
/** Branches, warehouses and registers (docs/specs/locations.md, L-01 … L-04). */
export const LOCATION_TYPES = ["branch", "warehouse"];
export const locationTypeSchema = z.enum(LOCATION_TYPES);
export const ACTIVE_STATUSES = ["active", "inactive"];
export const activeStatusSchema = z.enum(ACTIVE_STATUSES);
/** The branch part of POS bill numbers (`B1` in `B1-P2-000123`): 1–6 letters/digits, upper-cased on input. */
export const billPrefixSchema = z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .pipe(z.string().regex(/^[A-Z0-9]{1,6}$/));
const locationName = z.string().trim().min(1).max(100);
const locationText = z.string().trim().max(500);
const registerName = z.string().trim().min(1).max(50);
/** ST-11 (A8): opening hours as the merchant writes them (`Sat–Thu 10:00–21:00`). */
const openingHours = z.string().trim().max(200);
/** ST-11 (A8): a map link for shoppers (Google Maps or any `https:` address). */
const mapUrl = z
    .string()
    .trim()
    .max(500)
    .regex(/^https:\/\/[^\s]+$/, "Use an https:// link");
export const registerSchema = z.object({
    id: z.uuid(),
    locationId: z.uuid(),
    name: z.string(),
    status: activeStatusSchema,
    version: z.int(),
    createdAt: z.iso.datetime(),
});
export const locationSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    type: locationTypeSchema,
    billPrefix: z.string().nullable(),
    address: z.string(),
    phone: z.string().nullable(),
    receiptHeader: z.string(),
    isDefaultFulfilment: z.boolean(),
    /** ST-11 (A8): listed on the online shop (store locator, footer). Off until the merchant turns it on. */
    shopVisible: z.boolean().default(false),
    openingHours: z.string().default(""),
    mapUrl: z.string().nullable().default(null),
    status: activeStatusSchema,
    deactivatedAt: z.iso.datetime().nullable(),
    version: z.int(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const locationDetailSchema = locationSchema.extend({ registers: z.array(registerSchema) });
/** Every location the caller may see (a tenant has a handful; no paging). */
export const locationListSchema = z.object({ data: z.array(locationSchema), nextCursor: z.null() });
export const locationListQuerySchema = z.object({
    type: locationTypeSchema.optional(),
    status: activeStatusSchema.optional(),
});
const locationFields = {
    name: locationName,
    address: locationText.optional(),
    phone: businessPhoneSchema.nullable().optional(),
    receiptHeader: locationText.optional(),
    shopVisible: z.boolean().optional(),
    openingHours: openingHours.optional(),
    mapUrl: mapUrl.nullable().optional(),
};
/** `POST /v1/locations`: branches need a bill prefix; warehouses have none. */
export const createLocationRequestSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("branch"), billPrefix: billPrefixSchema, ...locationFields }).strict(),
    z.object({ type: z.literal("warehouse"), ...locationFields }).strict(),
]);
/** `PATCH /v1/locations/{id}`: the changed fields and the `version` last read (409 if it moved on). Type is fixed. */
export const updateLocationRequestSchema = z
    .object({
    name: locationName.optional(),
    address: locationText.optional(),
    phone: businessPhoneSchema.nullable().optional(),
    receiptHeader: locationText.optional(),
    billPrefix: billPrefixSchema.optional(),
    shopVisible: z.boolean().optional(),
    openingHours: openingHours.optional(),
    mapUrl: mapUrl.nullable().optional(),
    version: z.int().min(1),
})
    .strict()
    .refine((change) => Object.keys(change).some((key) => key !== "version"), "Nothing to change");
export const createRegisterRequestSchema = z.object({ name: registerName }).strict();
export const updateRegisterRequestSchema = z.object({ name: registerName, version: z.int().min(1) }).strict();
// Endpoints -------------------------------------------------------------------------------------------------
const locations = { tag: "locations" };
const manage = { permission: "locations.manage" };
export const listLocations = defineEndpoint({
    ...locations,
    name: "listLocations",
    method: "GET",
    path: "/v1/locations",
    access: { permission: "locations.view" },
    summary: "Branches and warehouses the caller may see",
    query: locationListQuerySchema,
    status: 200,
    response: locationListSchema,
});
export const createLocation = defineEndpoint({
    ...locations,
    name: "createLocation",
    method: "POST",
    path: "/v1/locations",
    access: manage,
    summary: "Add a branch or warehouse (the first becomes the default fulfilment location)",
    body: createLocationRequestSchema,
    status: 201,
    response: locationDetailSchema,
    errors: ["CONFLICT", "PLAN_LIMIT_REACHED"],
    entitlement: "locations.max",
});
export const getLocation = defineEndpoint({
    ...locations,
    name: "getLocation",
    method: "GET",
    path: "/v1/locations/{id}",
    access: { permission: "locations.view", locationParam: "id" },
    summary: "One location with its registers",
    params: idParamsSchema,
    status: 200,
    response: locationDetailSchema,
});
export const updateLocation = defineEndpoint({
    ...locations,
    name: "updateLocation",
    method: "PATCH",
    path: "/v1/locations/{id}",
    access: manage,
    summary: "Change a location (optimistic version)",
    params: idParamsSchema,
    body: updateLocationRequestSchema,
    status: 200,
    response: locationDetailSchema,
    errors: ["CONFLICT"],
});
export const deactivateLocation = defineEndpoint({
    ...locations,
    name: "deactivateLocation",
    method: "POST",
    path: "/v1/locations/{id}/deactivate",
    access: manage,
    summary: "Deactivate a location and its registers (not the default fulfilment location; not while in use)",
    params: idParamsSchema,
    status: 200,
    response: locationDetailSchema,
    errors: ["LOCATION_IN_USE", "BUSINESS_RULE_VIOLATION"],
});
export const activateLocation = defineEndpoint({
    ...locations,
    name: "activateLocation",
    method: "POST",
    path: "/v1/locations/{id}/activate",
    access: manage,
    summary: "Activate a location again (its registers stay inactive until activated)",
    params: idParamsSchema,
    status: 200,
    response: locationDetailSchema,
    errors: ["PLAN_LIMIT_REACHED"],
    entitlement: "locations.max",
});
export const setDefaultFulfilmentLocation = defineEndpoint({
    ...locations,
    name: "setDefaultFulfilmentLocation",
    method: "POST",
    path: "/v1/locations/{id}/default-fulfilment",
    access: manage,
    summary: "Make this active location the default for online orders",
    params: idParamsSchema,
    status: 200,
    response: locationDetailSchema,
    errors: ["BUSINESS_RULE_VIOLATION"],
});
export const createRegister = defineEndpoint({
    ...locations,
    name: "createRegister",
    method: "POST",
    path: "/v1/locations/{id}/registers",
    access: manage,
    summary: "Add a register (cash counter) to an active branch",
    params: idParamsSchema,
    body: createRegisterRequestSchema,
    status: 201,
    response: registerSchema,
    errors: ["CONFLICT", "BUSINESS_RULE_VIOLATION"],
});
export const updateRegister = defineEndpoint({
    ...locations,
    name: "updateRegister",
    method: "PATCH",
    path: "/v1/registers/{id}",
    access: manage,
    summary: "Rename a register (optimistic version)",
    params: idParamsSchema,
    body: updateRegisterRequestSchema,
    status: 200,
    response: registerSchema,
    errors: ["CONFLICT"],
});
export const deactivateRegister = defineEndpoint({
    ...locations,
    name: "deactivateRegister",
    method: "POST",
    path: "/v1/registers/{id}/deactivate",
    access: manage,
    summary: "Deactivate a register",
    params: idParamsSchema,
    status: 200,
    response: registerSchema,
});
export const activateRegister = defineEndpoint({
    ...locations,
    name: "activateRegister",
    method: "POST",
    path: "/v1/registers/{id}/activate",
    access: manage,
    summary: "Activate a register again (its branch must be active)",
    params: idParamsSchema,
    status: 200,
    response: registerSchema,
    errors: ["BUSINESS_RULE_VIOLATION"],
});
//# sourceMappingURL=locations.js.map